/* ============================================================
   DNB Instant Cart Drawer v7 (2026-08-18)

   GOAL: make the click register instantly. Nothing more.

   Measured on live, "Add to cart" took 1,148ms before the drawer
   appeared (coffee PDP) and ~3,300ms before the bundle was fully
   drawn. That is two sequential server round trips - POST
   /cart/add.js (~500-1000ms, of which ~400ms is a fixed cart-write
   cost we cannot remove from the theme) then GET /cart to render
   the drawer. For a full second the customer has no feedback at all.

   WHAT IT DOES: on any add, puts the drawer into a loading state and
   opens it immediately - one shimmering placeholder row, and shimmer
   where the count, free-shipping bar and subtotal will be. It does
   NOT predict what is being added: no product data, no prices, no
   gift list, no cache. The real rows arrive from the theme about a
   second later and take over.

   WHY IT IS BUILT THIS WAY: predicting cart contents client-side
   means owning every way that prediction can be wrong - subscription
   pricing, quantity merges, bundle gifts added in a later request,
   sold-out rejections, discounts. Showing "loading" asserts nothing,
   so none of those cases exist. The only claim on screen is "we
   heard you", which is always true.

   v3 CHANGE - why the loading state is CSS, not classes on elements:
   the theme re-renders the drawer's contents during the load (more
   than once on a bundle add). Any class we put on an individual
   element - the empty-cart block, the totals - is destroyed by that
   re-render, and the stale "Your Cart is Empty" / "Subtotal Free"
   markup pops back for a frame. So the ONLY thing we toggle is a
   single .dnb-loading class on <cart-drawer> itself, which the theme
   never replaces. Every rule below hangs off that ancestor, so freshly
   rendered markup is covered the instant it appears. Nothing to lose,
   nothing to re-apply.

   v4 CHANGE: always draw the placeholder row, including when the
   drawer is already open with items in it. Previously a second add
   only shimmered the totals, which was too quiet to read as "the
   click landed".

   v5 CHANGE: settle grace cut 600ms -> 250ms, see SETTLE_GRACE_MS.
   Normal adds show their real totals ~350ms sooner; a bundle add now
   shows a second, honest loading phase when its free gifts land.

   v6 was an attempt to hide the mid-add empty-cart flash with extra
   CSS selectors and inline display:none. It has been REVERTED - it
   treated the symptom, and its selectors (.cart__price__loader,
   .cart__total) also matched the drawer's own totals and the markup
   Loop's widget lives in. This file is v5 again apart from the nudge
   fix below.

   v7 CHANGE - the mid-add "Your Cart is Empty" flash.
   The flashing markup was never this drawer's empty state. It came
   from GET /cart?section_id=api-cart-items: when that section had no
   [data-api-line-items] / [data-api-upsell-items] wrappers (its old
   empty-cart output), theme.js build() blitted the WHOLE section into
   <cart-items data-items-holder> - empty-cart block, a duplicate
   "Subtotal / Free" row and a duplicate orange loader bar. It is
   normally masked by .is-empty on the drawer, which hold() below
   removes on purpose, so during an add it became readable.

   That is fixed at the source, in sections/api-cart-items.liquid,
   which now always emits both wrappers. The only change HERE is to
   stop CAUSING such a render: nudge() used to ask the theme to
   refresh 1800ms after the add started even when the add was still
   in flight - i.e. before the cart had been written - so the section
   came back describing the PRE-add cart. On a slow mobile connection
   that is the normal case, not an edge case. It now waits for the
   add to land first. See nudge().

   HOW: chains on window.fetch and reacts to POST /cart/add.js. That
   is the one chokepoint shared by both add paths on this store (the
   theme's FormData post on standard PDPs, Loop's JSON post on the
   bundle PDP), so it needs no theme or Loop edits. The request body
   is never parsed - only the fact that an add happened matters.

   SAFETY: fails open. No drawer, no cart drawer in the theme, or any
   error at all = nothing happens and stock behaviour runs unchanged.
   No network calls of its own (except one self-heal refresh), no
   libraries, no storage, no blocking work.

   Health readout in the console: __dnbICD
   ============================================================ */
(function () {
  'use strict';

  var DRAWER_SEL = '#cart-drawer';
  var ITEMS_SEL  = 'cart-items[data-items-holder]';

  /* Small grace after the theme renders, purely to absorb the theme
     rendering the drawer twice in quick succession for one add.

     It is deliberately NOT long enough to cover the bundle's second
     request: measured over 3 runs, the guard adds the 3 free gifts
     1585 / 1799 / 1787 ms after the first render lands. Stretching the
     grace to cover that would mean ~2s of shimmering totals on EVERY
     add to cart, which is the opposite of the point. So a bundle add
     shows two loading phases - children, then gifts - which is simply
     true: the cart really is being written to again. Row counts only
     ever go up, and the subtotal reads the same before and after
     (the gifts are free). */
  var SETTLE_GRACE_MS = 250;
  var NUDGE_AFTER_MS  = 1800;  /* ask the theme to refresh if it hasn't */
  var NUDGE_RETRY_MS  = 600;   /* ...but never while an add is in flight */
  var GIVE_UP_MS      = 8000;  /* stop shimmering no matter what */
  var HOLD_MS         = 200;   /* re-assert the loading shape */

  /* state: 'idle'    = installed, waiting for an add
            'loading' = drawer open, placeholder showing
            'off'     = no cart drawer on this page (e.g. /cart) */
  var S = window.__dnbICD = {
    v: 7, state: 'idle',
    opens: 0, settles: 0, reverts: 0, timeouts: 0, nudges: 0, nudgesDeferred: 0, errors: 0
  };

  function q(sel, root) {
    try { return (root || document).querySelector(sel); } catch (e) { return null; }
  }

  var L = null; /* live loading session, or null */

  /* One placeholder row, using the theme's own .cart__item classes so it
     inherits the real row's spacing and never looks bolted on. */
  var ROW =
    '<div class="cart__item dnb-ph" data-dnb-ph>' +
      '<div class="cart__item__image"><span class="dnb-sk dnb-sk--img"></span></div>' +
      '<div class="cart__item__content"><div class="cart__item__content-inner">' +
        '<h4 class="cart__item__title"><span class="dnb-sk dnb-sk--line"></span></h4>' +
        '<div class="cart__item__meta"><span class="dnb-sk dnb-sk--line dnb-sk--short"></span></div>' +
      '</div><p class="cart__price"><span class="dnb-sk dnb-sk--chip"></span></p></div>' +
    '</div>';

  function addPlaceholder(drawer) {
    var holder = q(ITEMS_SEL, drawer);
    if (!holder) return;
    try { holder.insertAdjacentHTML('beforeend', ROW); } catch (e) { S.errors++; }
  }

  /* Idempotent. Called after every drawer mutation, so a mid-load
     re-render by the theme cannot leave the drawer looking empty or
     showing a stale empty-cart state. It only RE-adds a placeholder if
     the drawer has no rows at all - once the real rows land we stop
     putting one back. */
  function hold(drawer) {
    /* the drawer refuses to render while it thinks the cart is empty */
    if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
    var holder = q(ITEMS_SEL, drawer);
    if (!holder) return;
    if (!holder.querySelector('.cart__item')) addPlaceholder(drawer);
  }

  function begin() {
    var drawer = q(DRAWER_SEL);
    if (!drawer) return;

    /* another add landed while we are already loading: just count it,
       the drawer is already open and already shimmering */
    if (L) { L.inflight++; cancelSettle(); return; }

    if (!q(ITEMS_SEL, drawer)) return;

    var wasEmpty = drawer.classList.contains('is-empty');

    /* FIRST, before any markup changes: one class, on the one element
       the theme never replaces. Everything the customer must not read
       mid-load is hidden by CSS from this instant on. */
    drawer.classList.add('dnb-loading');
    /* Always draw a placeholder row, even when the drawer is already open
       with items in it - otherwise a second add gives no visible sign
       that the click landed until the server answers a second later. */
    addPlaceholder(drawer);
    hold(drawer);

    var weOpened = false;
    if (!drawer.classList.contains('is-open')) {
      try {
        if (typeof drawer.openCartDrawer === 'function') drawer.openCartDrawer();
        else drawer.classList.add('is-open');
        weOpened = true;
      } catch (e) { S.errors++; }
    }

    L = {
      drawer: drawer, weOpened: weOpened, wasEmpty: wasEmpty,
      inflight: 1, rendered: false, settleTimer: null,
      keep: setInterval(function () { if (L) hold(L.drawer); }, HOLD_MS),
      nudge: setTimeout(nudge, NUDGE_AFTER_MS),
      giveUp: setTimeout(function () { S.timeouts++; end(true); }, GIVE_UP_MS)
    };
    S.opens++; S.state = 'loading';
  }

  /* Normally the theme refreshes the drawer itself after an add. If
     something adds to the cart without triggering that refresh (a
     third-party app posting straight to /cart/add.js, say), nothing
     would ever arrive and we would shimmer until the give-up timer.
     Ask the theme to refresh instead.

     v7: NEVER while an add is still in flight. getCart() reads
     /cart?section_id=api-cart-items, and Shopify answers it from the
     cart as it stands at that instant - so asking mid-add returns the
     PRE-add cart, which for a first add is an empty one. The theme
     then renders that empty cart into the drawer the customer is
     watching. On a slow mobile connection the add routinely takes
     longer than NUDGE_AFTER_MS, so this fired often. Re-arm instead;
     giveUp still bounds the session, and the add's own then/catch
     always decrements inflight, so this cannot spin forever. */
  function nudge() {
    if (!L || L.rendered) return;
    if (L.inflight > 0) {
      S.nudgesDeferred++;
      L.nudge = setTimeout(nudge, NUDGE_RETRY_MS);
      return;
    }
    S.nudges++;
    try {
      var ci = document.querySelector('cart-items');
      if (ci && typeof ci.getCart === 'function') ci.getCart();
    } catch (e) { S.errors++; }
  }

  function cancelSettle() {
    if (L && L.settleTimer) { clearTimeout(L.settleTimer); L.settleTimer = null; }
  }

  /* Clear the loading state once nothing is in flight and the drawer has
     been redrawn - after a short grace, so the theme rendering twice for
     one add does not flicker the shimmer off and back on. */
  function maybeSettle() {
    if (!L || L.inflight > 0 || !L.rendered) return;
    cancelSettle();
    L.settleTimer = setTimeout(function () { S.settles++; end(false); }, SETTLE_GRACE_MS);
  }

  /* restore=true: nothing landed, put the drawer back exactly as found */
  function end(restore) {
    if (!L) return;
    var s = L; L = null;
    clearInterval(s.keep);
    clearTimeout(s.nudge); clearTimeout(s.giveUp);
    if (s.settleTimer) clearTimeout(s.settleTimer);

    try {
      var d = s.drawer;
      var phs = d.querySelectorAll('[data-dnb-ph]');
      for (var i = 0; i < phs.length; i++) if (phs[i].parentNode) phs[i].parentNode.removeChild(phs[i]);
      d.classList.remove('dnb-loading');

      if (restore && !d.querySelector('.cart__item')) {
        S.reverts++;
        if (s.wasEmpty) d.classList.add('is-empty');
        if (s.weOpened) {
          if (typeof d.closeCartDrawer === 'function') d.closeCartDrawer();
          else d.classList.remove('is-open'); /* mirror of the open fallback above */
        }
      }
    } catch (e) { S.errors++; }

    S.state = 'idle';
  }

  /* Watch the drawer for the theme's own render. Observed at drawer
     level, not on <cart-items>, because the theme may replace that
     element wholesale rather than just its contents. */
  function watch() {
    var drawer = q(DRAWER_SEL);
    if (!drawer) return;
    new MutationObserver(function (muts) {
      if (!L) return;
      var sawReal = false;
      for (var i = 0; i < muts.length && !sawReal; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.hasAttribute && n.hasAttribute('data-dnb-ph')) continue;
          var real = (n.classList && n.classList.contains('cart__item') && !n.hasAttribute('data-dnb-ph'));
          if (!real && n.querySelector) real = !!n.querySelector('.cart__item:not([data-dnb-ph])');
          if (real) { sawReal = true; break; }
        }
      }
      /* runs as a microtask, i.e. BEFORE the browser paints - so a
         re-render that momentarily emptied the drawer never reaches
         the screen looking empty */
      hold(L.drawer);
      if (sawReal) { L.rendered = true; maybeSettle(); }
    }).observe(drawer, { childList: true, subtree: true });
  }

  /* ---------- styles ----------
     Every rule hangs off .dnb-loading on <cart-drawer>. That element is
     never replaced by the theme, so markup rendered mid-load is covered
     the moment it appears - there is nothing to re-apply and nothing to
     lose. Kept in this file so the CSS can never drift from the markup.

     The totals, free-shipping bar and count still hold PRE-add values
     while we wait (an empty cart literally renders "Subtotal Free").
     Dimming is not enough - a wrong number stays readable - so their
     contents are hidden and a shimmer is drawn in their place. Height is
     preserved. !important because the theme's selectors are more
     specific. color:transparent covers bare text nodes, which
     visibility on child elements cannot reach.

     Deliberately NOT extended to .cart__total or .cart__price__loader:
     those live in the drawer's own totals block and in Loop's widget
     markup as well, so blanket rules for them reach far outside this
     loading state. The duplicate copies that used to appear inside the
     item list are gone at the source - see sections/api-cart-items.liquid. */
  var CSS =
    '@keyframes dnbSk{0%{background-position:-200px 0}100%{background-position:calc(200px + 100%) 0}}' +
    '.dnb-sk{display:inline-block;border-radius:4px;background:#ececec;' +
      'background-image:linear-gradient(90deg,#ececec 0,#f7f7f7 40px,#ececec 80px);' +
      'background-size:200px 100%;background-repeat:no-repeat;animation:dnbSk 1.1s ease-in-out infinite}' +
    '.dnb-sk--img{width:64px;height:64px;border-radius:6px}' +
    '.dnb-sk--line{width:70%;height:12px;margin:2px 0}' +
    '.dnb-sk--short{width:40%;height:10px}' +
    '.dnb-sk--chip{width:52px;height:13px}' +
    '.dnb-loading [data-cart-empty],' +
    '.dnb-loading .drawer__empty{display:none!important}' +
    '.dnb-loading [data-cart-price-holder],' +
    '.dnb-loading .free-shipping,' +
    '.dnb-loading .cart__items-count{position:relative!important;pointer-events:none!important;' +
      'color:transparent!important;min-height:14px}' +
    '.dnb-loading [data-cart-price-holder] > *,' +
    '.dnb-loading .free-shipping > *,' +
    '.dnb-loading .cart__items-count > *{visibility:hidden!important}' +
    '.dnb-loading [data-cart-price-holder]::after,' +
    '.dnb-loading .free-shipping::after,' +
    '.dnb-loading .cart__items-count::after{content:"";position:absolute;right:0;top:50%;' +
      'transform:translateY(-50%);width:80px;height:13px;border-radius:4px;' +
      'background:#ececec;background-image:linear-gradient(90deg,#ececec 0,#f7f7f7 40px,#ececec 80px);' +
      'background-size:200px 100%;background-repeat:no-repeat;animation:dnbSk 1.1s ease-in-out infinite}' +
    '.dnb-loading .cart__items-count{display:inline-block;min-width:18px}' +
    '.dnb-loading .cart__items-count::after{width:16px;height:11px}' +
    '@media (prefers-reduced-motion:reduce){' +
      '.dnb-sk,.dnb-loading [data-cart-price-holder]::after,' +
      '.dnb-loading .free-shipping::after,.dnb-loading .cart__items-count::after{animation:none}}';

  function injectCSS() {
    try {
      if (document.getElementById('dnb-icd-css')) return;
      var st = document.createElement('style');
      st.id = 'dnb-icd-css';
      st.textContent = CSS;
      (document.head || document.documentElement).appendChild(st);
    } catch (e) { S.errors++; }
  }

  function install() {
    if (!q(DRAWER_SEL) || (window.theme && window.theme.hasCartDrawer === false)) { S.state = 'off'; return; }

    injectCSS();
    watch();

    var orig = window.fetch;
    if (typeof orig !== 'function') { S.state = 'off'; return; }

    window.fetch = function (input, init) {
      var url = '', method = '';
      try {
        url = String((input && input.url) || input || '');
        method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      } catch (e) {}

      var p = orig.apply(this, arguments);

      try {
        if (method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url) &&
            window.location.pathname.indexOf('/a/loop_subscriptions') === -1) {
          /* synchronous: the drawer enters the loading state in this
             same click frame, before anything is painted */
          begin();
          p.then(function (res) {
            if (!L) return;
            L.inflight--;
            if (res && res.ok) maybeSettle();
            else if (L.inflight <= 0 && !L.rendered) end(true); /* rejected: put it back */
          }).catch(function () {
            if (!L) return;
            L.inflight--;
            if (L.inflight <= 0 && !L.rendered) end(true);
          });
        }
      } catch (e) { S.errors++; }

      return p;
    };

    S.state = 'idle';
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install);
  else install();
})();
